/**
 * #/competition/:id — la ficha de un torneo, con sus tres vistas.
 *
 * Es la pantalla que empata lo que la federación ya tiene con Web Competition
 * Manager: fixture, tabla y ficha, más las dos acciones que hoy hacen falta
 * para que eso exista de verdad —generar el calendario y **enganchar los
 * partidos que ya están en el archivo**—, que sólo se muestran a quien tiene el
 * permiso (`competitions:manage`).
 *
 * La tabla y el fixture se piden por separado a propósito: son dos endpoints y
 * dos costos distintos, y la pestaña que no se está mirando no se paga.
 */
import { useState } from 'react';
import type { MessageKey } from '../i18n/index';
import { useT } from '../i18n/use-t';
import { Card, Empty, ErrorBox, Loading } from '../components/States';
import { errorMessage, useAsync } from '../hooks';
import type { ApiClient, Identity } from '../lib/api-client';
import { canManageCompetitions } from '../lib/auth';
import { COMPETITION_TABS, competitionHash, type CompetitionTab } from '../lib/router';
import { CompetitionForm } from './competition/CompetitionForm';
import { FixtureTab } from './competition/FixtureTab';
import { InfoTab } from './competition/InfoTab';
import { LinkMatchesCard } from './competition/LinkMatchesCard';
import { StandingsTab } from './competition/StandingsTab';

const TAB_KEYS: Record<CompetitionTab, MessageKey> = {
  fixture: 'competition.tabFixture',
  posiciones: 'competition.tabStandings',
  ficha: 'competition.tabInfo',
};

export function CompetitionPage({
  client,
  competitionId,
  tab,
  identity,
}: {
  client: ApiClient;
  competitionId: string;
  tab: CompetitionTab;
  identity: Identity | null;
}): JSX.Element {
  const t = useT();
  const competition = useAsync(
    () => client.getCompetition(competitionId),
    [client, competitionId],
  );
  const fixture = useAsync(() => client.getFixture(competitionId), [client, competitionId]);
  const standings = useAsync(() => client.getStandings(competitionId), [client, competitionId]);

  const puedeGestionar = canManageCompetitions(identity);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** La ficha se edita ACÁ mismo: es donde se la está mirando. */
  const [editando, setEditando] = useState(false);

  const recargar = (): void => {
    competition.reload();
    fixture.reload();
    standings.reload();
  };

  const generar = async (phaseId: string, legs: 1 | 2): Promise<void> => {
    setBusy(true);
    setNotice(null);
    try {
      const page = await client.generateFixture(competitionId, { phaseId, legs });
      setNotice(t('competition.generateDone', { count: page.count }));
      recargar();
    } catch (err) {
      setNotice(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(false);
    }
  };

  if (competition.status === 'error') {
    return (
      <div>
        <ErrorBox message={errorMessage(competition.error)} onRetry={competition.reload} />
        <Card>
          <Empty>
            <a href="#/competitions">{t('competition.backToList')}</a>
          </Empty>
        </Card>
      </div>
    );
  }

  const ficha = competition.data;

  return (
    <div>
      <div className="page-head">
        <h1>{ficha?.name ?? competitionId}</h1>
        <div className="row">
          <a href="#/competitions" style={{ textDecoration: 'none' }}>
            <button type="button" className="small">
              {t('competition.backToList')}
            </button>
          </a>
          <button type="button" onClick={recargar}>
            {t('common.refresh')}
          </button>
        </div>
      </div>
      {ficha !== null ? (
        <p className="hint">
          {ficha.season} · {ficha.teams.length} {t('competition.colTeams').toLowerCase()}
        </p>
      ) : null}

      <nav className="tabs">
        {COMPETITION_TABS.map((name) => (
          <a
            key={name}
            href={competitionHash(competitionId, name)}
            style={{ textDecoration: 'none' }}
          >
            <button type="button" className={`tab${tab === name ? ' active' : ''}`}>
              {t(TAB_KEYS[name])}
            </button>
          </a>
        ))}
      </nav>

      {notice !== null ? <p className="hint">{notice}</p> : null}

      {/* Armado del calendario: sólo para quien puede (el API igual lo exige). */}
      {puedeGestionar && tab === 'fixture' && ficha !== null && ficha.phases.length > 0 ? (
        <Card title={t('competition.generate')}>
          <p className="hint">{t('competition.generateHint')}</p>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            {ficha.phases.map((phase) => (
              <span key={phase.id} className="row" style={{ gap: 4 }}>
                <button
                  type="button"
                  className="small"
                  disabled={busy}
                  onClick={() => void generar(phase.id, 1)}
                >
                  {phase.name} · {t('competition.legsOne')}
                </button>
                <button
                  type="button"
                  className="small"
                  disabled={busy}
                  onClick={() => void generar(phase.id, 2)}
                >
                  {phase.name} · {t('competition.legsTwo')}
                </button>
              </span>
            ))}
          </div>
          {busy ? <p className="hint">{t('competition.generating')}</p> : null}
        </Card>
      ) : null}

      {/* Enganche del histórico: los partidos que ya están en el archivo. */}
      {puedeGestionar && tab === 'fixture' && ficha !== null ? (
        <LinkMatchesCard client={client} competition={ficha} onLinked={recargar} />
      ) : null}

      {tab === 'fixture' ? (
        <>
          {fixture.status === 'error' ? (
            <ErrorBox message={errorMessage(fixture.error)} onRetry={fixture.reload} />
          ) : null}
          {fixture.status === 'loading' && fixture.first ? (
            <Loading what={t('competition.loadingCompetition')} />
          ) : (
            <FixtureTab competition={ficha} fixture={fixture.data?.fixture ?? []} />
          )}
        </>
      ) : null}

      {tab === 'posiciones' ? (
        <>
          {standings.status === 'error' ? (
            <ErrorBox message={errorMessage(standings.error)} onRetry={standings.reload} />
          ) : null}
          {standings.status === 'loading' && standings.first ? (
            <Loading what={t('competition.loadingCompetition')} />
          ) : (
            <StandingsTab blocks={standings.data?.blocks ?? []} />
          )}
        </>
      ) : null}

      {tab === 'ficha' ? (
        <>
          {competition.status === 'loading' && competition.first ? (
            <Loading what={t('competition.loadingCompetition')} />
          ) : null}
          {/* Editar la ficha desde la ficha: el mismo formulario del alta, para
              que no haya dos formularios que se desincronicen. */}
          {ficha !== null && editando ? (
            <CompetitionForm
              client={client}
              initial={ficha}
              onSaved={(saved) => {
                setNotice(t('competitionForm.savedNotice', { name: saved.name }));
                setEditando(false);
                recargar();
              }}
              onCancel={() => setEditando(false)}
            />
          ) : null}
          {ficha !== null && !editando ? (
            <>
              {puedeGestionar ? (
                <div className="row" style={{ marginBottom: 12 }}>
                  <button type="button" className="primary" onClick={() => setEditando(true)}>
                    {t('competitionForm.edit')}
                  </button>
                </div>
              ) : null}
              <InfoTab competition={ficha} />
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
